import React, { useCallback, useEffect, useState } from "react";
import api, { authFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileText, ChevronDown, ChevronUp } from "lucide-react";
import BarraFiltri from "@/components/BarraFiltri";

export default function Storico() {
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ employee_id: "", item_id: "", date_from: "", date_to: "" });
  const [expand, setExpand] = useState({});
  // I filtri qui sotto restringono per persona, articolo e periodo: la
  // ricerca serve per il resto, il numero della consegna o chi l'ha fatta.
  const [cerca, setCerca] = useState("");

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k,v]) => v && params.append(k, v));
    const { data } = await api.get(`/deliveries?${params.toString()}`);
    setList(data);
  }, [f]);
  useEffect(() => {
    api.get("/employees").then(({data}) => setEmployees(data));
    api.get("/items").then(({data}) => setItems(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = () => {
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k,v]) => v && params.append(k, v));
    authFetch(`/reports/deliveries/pdf?${params.toString()}`)
      .then(r => r.blob())
      .then(b => {
        const u = window.URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = u; a.download = `report_${Date.now()}.pdf`; a.click();
        window.URL.revokeObjectURL(u);
      });
  };

  const q = cerca.trim().toLowerCase();
  const visibili = q
    ? list.filter((d) => `${d.id} ${d.employee_name} ${d.operator_email}`
        .toLowerCase().includes(q))
    : list;

  return (
    <div className="space-y-6" data-testid="storico-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">DPI</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Storico consegne</h1>
          <p className="text-sm text-slate-500 mt-1">{list.length} consegne registrate</p>
        </div>
        <Button className="bg-orange-500 hover:bg-orange-600 text-white h-12" onClick={downloadPdf} data-testid="pdf-export">
          <Download className="w-4 h-4 mr-2" /> Esporta PDF
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Select value={f.employee_id || "all"} onValueChange={(v)=>setF({...f, employee_id: v === "all" ? "" : v})}>
            <SelectTrigger><SelectValue placeholder="Dipendente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i dipendenti</SelectItem>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.item_id || "all"} onValueChange={(v)=>setF({...f, item_id: v === "all" ? "" : v})}>
            <SelectTrigger><SelectValue placeholder="Articolo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli articoli</SelectItem>
              {items.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={f.date_from} onChange={(e)=>setF({...f, date_from: e.target.value})} />
          <Input type="date" value={f.date_to} onChange={(e)=>setF({...f, date_to: e.target.value})} />
        </CardContent>
      </Card>

      <BarraFiltri cerca={cerca} onCerca={setCerca}
                   segnaposto="Numero, dipendente o operatore..."
                   conteggio={visibili.length} etichetta="consegne" />

      <div className="space-y-2">
        {visibili.length === 0 && <p className="text-center text-slate-500 py-8">Nessuna consegna trovata.</p>}
        {visibili.map(d => (
          <Card key={d.id} className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-3 cursor-pointer"
                   onClick={() => setExpand({...expand, [d.id]: !expand[d.id]})}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline">{d.id}</Badge>
                    <span className="font-bold">{d.employee_name}</span>
                    <span className="text-xs text-slate-500">{new Date(d.delivered_at).toLocaleString("it-IT")}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {d.lines.length} articoli · op. {d.operator_email}
                  </div>
                </div>
                {expand[d.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
              {expand[d.id] && (
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-1">
                  {d.lines.map((l) => (
                    <div key={`${d.id}-${l.item_id}-${l.size}`} className="flex justify-between text-sm">
                      <span>{l.name} · <Badge variant="outline">{l.size}</Badge></span>
                      <span className="font-bold">x{l.quantity}</span>
                    </div>
                  ))}
                  {d.notes && <div className="text-xs text-slate-500 mt-2">Note: {d.notes}</div>}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
